import { Component, Input, OnInit, ViewChild, Output, EventEmitter, AfterViewInit, ElementRef, NgZone } from '@angular/core';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { v4 as uuidv4 } from 'uuid';
import { ApiService } from '../shared/api.service';


@Component({
  selector: 'app-location-dropdown',
  templateUrl: './location-dropdown.component.html',
  styleUrls: ['./location-dropdown.component.css']
})
export class LocationDropdownComponent implements OnInit, AfterViewInit {
  @ViewChild('waterSourceTrigger') autocomplete: MatAutocompleteTrigger;
  @ViewChild('inputField') inputField: ElementRef;

  @Input() apiUid: string;
  @Input() selectedItem;
  @Input() idToken: string;
  @Input() labelNeeded: boolean;
  @Input() focusNeeded: boolean;
  @Input() widthNeeded: boolean;
  @Input() clearNeeded: boolean;
  @Input() maxWidthNeeded: boolean;
  @Input() userSavedPlaces: Array<any>;
  @Output() getUserData = new EventEmitter<boolean>();
  @Output() setNewTestData = new EventEmitter<any>();
  lastTypedValue: string;
  placesService = new google.maps.places.PlacesService(document.createElement('div'));
  predictionsArray: any[] = [];
  autocompleteService = new google.maps.places.AutocompleteService();
  filteredUserSavedPlaces: any[];
  isDuplicatePlace = false;
  showLocationClear = false;
  private newTest = false;


  constructor(private apiService: ApiService, private ngZone: NgZone) {
  }

  ngOnInit(): void {
    this.lastTypedValue = this.selectedItem.location?.formatted_address ? this.selectedItem.location?.formatted_address :
      this.selectedItem.location?.name ? this.selectedItem.location?.name : '';
    this.filterUserPlaces(this.lastTypedValue);
    this.newTest = this.selectedItem.calculationResult.testId === undefined;
  }

  ngAfterViewInit() {
    if (this.focusNeeded) {
      this.inputField.nativeElement.focus();
    }
  }

  displayFn(value?): string {
    if (value) {
      return value.formatted_address ? value.formatted_address :
        value.description ? value.description :
          typeof value === 'string' ? value :
            this.lastTypedValue;
    }
    return '';
  }

  async handleAddressChange($event: MatAutocompleteSelectedEvent, testId) {
    const uniquePlaceId = uuidv4();
    const value = $event.option.value;
    const name = value.description || value.formatted_address || value.name || '';
    this.lastTypedValue = name;
    if (value.place_id !== 'custom_place') {
      const request = {
        placeId: value.place_id
      };
      this.placesService.getDetails(request, async details => {
        const data = {
          location: {
            place_id: details.place_id,
            name: details.name,
            formatted_address: details.formatted_address,
            lat: details.geometry.location.lat(),
            lng: details.geometry.location.lng(),
            waterSource: '' // Create nested waterSource property, to be filled after location is chosen.
          }
        };
        // Don't update test data if the dropdown is called from the new test modal
        if (!this.newTest) {
          await this.apiService.updateTestData(data, testId, this.apiUid, this.idToken);
          this.selectedItem.location = data.location;

        } else {
          this.setNewTestData.emit(data.location);
        }
      });

    } else {

      const data = {
        location: {
          place_id: $event.option.value.place_id === 'custom_place' ? uniquePlaceId : $event.option.value.place_id,
          name: $event.option.value.name,
          formatted_address: '',
          lat: 0,
          lng: 0,
          waterSource: '' // Create nested waterSource property, to be filled after location is chosen.
        }
      };
      if (!this.newTest) {
        await this.apiService.updateTestData(data, testId, this.apiUid, this.idToken);
        this.selectedItem.location = data.location;
      } else {
        this.setNewTestData.emit(data.location);
      }


    }

    if (this.userSavedPlaces.filter(place => place.place_id === value.place_id).length === 0) {
      if (value.place_id !== 'custom_place') {
        await this.savePlace(value.place_id);
      } else {
        const customPlace = {name, placeId: uniquePlaceId};
        await this.saveCustomPlace(customPlace);
      }
    }

  }


  async handleLocationChangeManual($event: any, testId: any) {
    const target = $event.target as HTMLInputElement;
    if (target.value === '') {

      if (this.userSavedPlaces.filter(place => place.place_id === 'no_location').length === 0 && !this.newTest) {
        await this.savePlace('no_location');
      }

      const data = {
        location: {
          place_id: 'no_location',
          name: 'No Location Chosen',
          formatted_address: 'No Location Chosen',
          lat: 0,
          lng: 0,
          waterSource: ''
        },
        waterSource: '' // Remove unnested waterSource property
      };

      // Update test data if not new test modal
      if (!this.newTest) {
        await this.apiService.updateTestData(data, testId, this.apiUid, this.idToken);
      } else {
        this.setNewTestData.emit(data.location);
      }
      if (this.autocomplete) {
        this.autocomplete.closePanel();
      }
    }
  }

  async savePlace(placeId: string) {
    const timestamp = Math.floor(Date.now() / 1000);
    if (placeId === 'no_location') {
      const data = {
        places:
          {
            place_id: placeId,
            name: 'No Location Chosen',
            formatted_address: 'No Location Chosen',
            lat: 0,
            lng: 0,
            time_created: timestamp
          }
      };
      await this.apiService.saveUserLocation(data, this.apiUid, this.idToken);
    } else {
      const request = {
        placeId
      };
      const selectedPrediction = this.predictionsArray.filter(place => place.place_id === placeId)[0];
      if (selectedPrediction !== undefined) {
        selectedPrediction.isSaving = true;
      }
      this.placesService.getDetails(request, async details => {
        const data = {
          places:
            {
              place_id: details.place_id,
              name: details.name,
              formatted_address: details.formatted_address,
              lat: details.geometry.location.lat(),
              lng: details.geometry.location.lng(),
              time_created: timestamp
            },
        };
        await this.apiService.saveUserLocation(data, this.apiUid, this.idToken);
        if (selectedPrediction !== undefined) {
          selectedPrediction.isSaving = false;
        }
        this.getUserData.emit(true);
        this.filterUserPlaces();
      });
    }
  }

  async saveCustomPlace({name, placeId}) {
    const timestamp = Math.floor(Date.now() / 1000);
    const data = {
      places:
        {
          place_id: placeId,
          name,
          formatted_address: '',
          lat: 0,
          lng: 0,
          time_created: timestamp
        }
    };
    await this.apiService.saveUserLocation(data, this.apiUid, this.idToken);
    this.getUserData.emit(true);
    this.filterUserPlaces();

  }

  displayPredictions(predictions) {
    const ngZone = this.ngZone; // necessary for ngZone to be defined. Do not inline
    ngZone.run(() => { // enter ngZone to fix autocomplete being 1 letter behind
      if (predictions) {
        const max = predictions.length > 3 ? 3 : predictions.length;
        for (let i = 0; i < max; i++) {
          this.predictionsArray[i] = predictions[i];
          this.predictionsArray[i].isSaving = false;
          this.predictionsArray[i].isSaved = this.userSavedPlaces.filter(place =>
            place.place_id === this.predictionsArray[i].place_id).length > 0;
        }
      }
    });

  }

  handleLocationInputChange(value) {
    this.filterUserPlaces(value);
    if (value !== '') {
      const request = {
        input: value,
      };
      this.autocompleteService.getPlacePredictions(request, this.displayPredictions.bind(this)); // Binding "this", so it's not undefined
    }
  }

  filterUserPlaces(value?) {
    const filterValue = value ? typeof value === 'string' ? value : '' : this.lastTypedValue ? this.lastTypedValue : '';
    this.filteredUserSavedPlaces = this.userSavedPlaces
      .filter(place => { // Filter by user input
        if (place.formatted_address && place.place_id) {
          return place.place_id !== 'no_location' && place.formatted_address.toLowerCase().includes(filterValue.toLowerCase()) ||
            place.place_id !== 'no_location' && place.name.toLowerCase().includes(filterValue.toLowerCase());
        }
        return place.place_id !== 'no_location' && place.name && place.name.toLowerCase().includes(filterValue.toLowerCase());
      })
      .sort( // Sort by most recent time created
        (a, b) => (a.time_created > b.time_created) ? -1 : ((b.time_created > a.time_created) ? 1 : 0)
      )
      .slice(0, 3); // Display max 3
    this.lastTypedValue = filterValue;

    // To prevent adding duplicate custom places
    this.isDuplicatePlace = this.userSavedPlaces.filter(place => {
      return place.name === this.lastTypedValue || place.formatted_address === this.lastTypedValue;
    }).length > 0;
  }

  clearLocationInput() {
    this.lastTypedValue = '';
    document.getElementById('autocompleteInput').focus();
  }

  async deletePlace($event: MouseEvent) {
    const target = $event.currentTarget as HTMLButtonElement;
    if (target.value) {

      const selectedPlace = this.userSavedPlaces.find(savedPlace => {
        return savedPlace.place_id === target.value;
      });

      const data = {
        places: selectedPlace
      };

      await this.apiService.deletePlace(data, this.apiUid, this.idToken);
      this.getUserData.emit(true);
      this.filterUserPlaces();
    }
  }

  /**
   * On input focus, show clear button
   * If the value is "No Location Chosen", clear the value
   * @param $event - input event
   */
  inputFocus($event: FocusEvent) {
    const inputElement = $event.currentTarget as HTMLInputElement;
    this.showLocationClear = true;
    if (inputElement.value === 'No Location Chosen') {
      this.lastTypedValue = '';
    }
  }
}
